import type { SyncStatus } from '@infra/sync/hybrid/HybridSyncEngine';

export type SyncTone = 'ok' | 'info' | 'warn' | 'error';

export interface SyncLabel {
  text: string;
  tone: SyncTone;
  /** Texte long (info-bulle, lecteur d'écran). */
  detail: string;
}

const READ_ONLY_REASONS: Record<string, string> = {
  license_expired: 'licence expirée',
  license_suspended: 'licence suspendue',
  license_revoked: 'licence révoquée',
  license_inactive: 'licence inactive',
};

function hhmm(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export function pendingText(n: number): string {
  return n === 1 ? '1 modification en attente' : `${n} modifications en attente`;
}

export function attentionText(n: number): string {
  return n === 1 ? '1 à examiner' : `${n} à examiner`;
}

/** Libellé de la pastille de la barre d'état (SYNC_DESIGN §6.9). Ne dépend d'aucune donnée métier. */
export function describeSyncStatus(status: SyncStatus): SyncLabel {
  const pending = status.pendingCount;
  switch (status.state) {
    case 'revoked':
      return { text: 'Poste révoqué', tone: 'error', detail: 'Ce poste a été révoqué : la synchronisation est arrêtée. Vos données locales et vos modifications en attente sont conservées.' };
    case 'paused_upgrade':
      return { text: 'Mise à jour requise', tone: 'warn', detail: 'Cette version de Nexxus est trop ancienne pour synchroniser. Mettez l’application à jour : vos modifications sont conservées.' };
    case 'needs_login':
      return { text: 'Reconnexion requise', tone: 'warn', detail: 'Reconnectez-vous pour reprendre la synchronisation. Votre travail local n’est pas interrompu.' };
    case 'disabled':
      return { text: 'Synchronisation désactivée', tone: 'info', detail: 'La synchronisation est arrêtée. Vos données restent sur ce poste.' };
    case 'read_only': {
      const reason = status.readOnlyReason ? (READ_ONLY_REASONS[status.readOnlyReason] ?? 'licence') : 'licence';
      return { text: `Lecture seule : ${reason}`, tone: 'warn', detail: `L’envoi est suspendu (${reason}). Vos modifications en attente sont conservées et partiront dès le renouvellement.` };
    }
    case 'offline': {
      const since = status.offlineSince ? ` depuis ${hhmm(status.offlineSince)}` : '';
      const text = pending > 0 ? `Hors ligne${since} — ${pending} en attente` : `Hors ligne${since}`;
      return { text, tone: 'warn', detail: 'Pas de connexion au serveur. Vous pouvez continuer à travailler : tout sera envoyé au retour du réseau.' };
    }
    case 'syncing':
      return { text: 'Synchronisation…', tone: 'info', detail: 'Échange des modifications avec le serveur.' };
    case 'error':
      return { text: pending > 0 ? pendingText(pending) : 'Synchronisation à reprendre', tone: 'warn', detail: 'Le serveur est momentanément indisponible. Nouvel essai automatique.' };
    default:
      break;
  }
  if (status.blockedDays > 0) {
    return { text: `Synchronisation bloquée depuis ${status.blockedDays} j`, tone: 'error', detail: 'Aucune synchronisation réussie depuis plus de 24 h alors que des modifications attendent.' };
  }
  if (pending > 0) return { text: pendingText(pending), tone: 'info', detail: 'Ces modifications partiront automatiquement.' };
  return { text: `En ligne — à jour ${hhmm(status.lastSuccessAt)}`, tone: 'ok', detail: 'Toutes vos modifications sont synchronisées.' };
}
